import Vue from 'vue';
import { captureException } from '@/plugins/error-reporting';

// --- Configuration ---------------------------------------------------------
// CURRENT_BUILD_ID is baked into the bundle at build time (see vue.config.js).
// It is intentionally undefined for `vue-cli-service serve` unless a
// VUE_APP_BUILD_ID env var is set, which makes the whole feature a no-op in
// local dev — there is nothing to compare against and nothing to poll for.
export const CURRENT_BUILD_ID = process.env.VUE_APP_BUILD_ID || null;

const VERSION_URL = '/version.json';
// Configurable via env (see vue.config.js defaults) so e2e tests can run the
// checker on a short cycle instead of waiting on production-sized intervals.
const POLL_INTERVAL_MS = Number(process.env.VUE_APP_VERSION_POLL_INTERVAL_MS) || 5 * 60 * 1000;
const ROUTE_CHECK_THROTTLE_MS = Number(process.env.VUE_APP_VERSION_ROUTE_THROTTLE_MS) || 60 * 1000;
// Auto-reload on route change is silent by design (see
// maybeAutoReloadOnRouteChange below) -- this is the safety net for a tab
// that never navigates again after a new build is detected (left open on a
// background tab, a single-page workflow, etc). Only fires the banner if
// still not on the new build after this long.
const BANNER_FALLBACK_DELAY_MS = Number(process.env.VUE_APP_VERSION_BANNER_FALLBACK_MS) || 10 * 60 * 1000;

const MAX_RELOADS_PER_WINDOW = 3;
const RELOAD_WINDOW_MS = 10 * 60 * 1000;

const STORAGE_PREFIX = 'permtang:appver:';
const KEY_TARGET_BUILD_ID = STORAGE_PREFIX + 'targetBuildId';
const KEY_RELOAD_TIMESTAMPS = STORAGE_PREFIX + 'reloadTimestamps';
const KEY_CHUNK_ERROR_RECOVERED = STORAGE_PREFIX + 'chunkErrorRecovered';
const KEY_AUTO_RELOAD_DISABLED = STORAGE_PREFIX + 'autoReloadDisabled';

// --- sessionStorage helpers (never let storage failures break the app) -----

function safeGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
}

function safeRemove(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // ignore
  }
}

function detectStorageAvailability() {
  const testKey = STORAGE_PREFIX + '__test__';
  try {
    window.sessionStorage.setItem(testKey, '1');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
}

const storageAvailable = detectStorageAvailability();

// --- Reactive state consumed by AppUpdateBanner.vue (added in a later phase) -

export const versionState = Vue.observable({
  // A newer build than the one currently running has been detected.
  updateAvailable: false,
  // The buildId of that newer build, or null.
  latestBuildId: null,
  // true once auto-reload has been permanently disabled for this tab
  // (safeguard tripped, or storage is unavailable) — the banner should show
  // a manual "update now" action instead of expecting auto-reload to happen.
  reloadBlocked: false,
  // true when a ChunkLoadError happened and could not be silently recovered
  // (recovery reload already used up) — the banner should offer a manual
  // reload for this too.
  chunkErrorNotice: false,
  // Drives AppUpdateBanner.vue's visibility for the "new build detected"
  // case. Auto-reload on route change is silent by design, so this only
  // flips true when auto can't be relied on to finish the job by itself:
  // immediately if reloadBlocked trips (see disableAutoReload below), or
  // after BANNER_FALLBACK_DELAY_MS if the tab just never navigates again.
  showUpdateBanner: false
});

let bannerFallbackScheduled = false;

function scheduleBannerFallback() {
  if (bannerFallbackScheduled) return;
  bannerFallbackScheduled = true;
  window.setTimeout(() => {
    // Only surface it if still true -- a reload that already succeeded (via
    // route-change navigation, in the meantime) tore down this whole JS
    // context anyway, so in practice this only fires when nothing else did.
    if (versionState.updateAvailable) {
      versionState.showUpdateBanner = true;
    }
  }, BANNER_FALLBACK_DELAY_MS);
}

// --- Reload safeguards -------------------------------------------------------
// These bound EVERY automatic reload this module can trigger, whether it
// comes from a detected version mismatch or from ChunkLoadError recovery.

function readReloadTimestamps() {
  const raw = safeGet(KEY_RELOAD_TIMESTAMPS);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(value => typeof value === 'number') : [];
  } catch (error) {
    return [];
  }
}

function reloadBudgetExceeded() {
  const now = Date.now();
  const recent = readReloadTimestamps().filter(timestamp => now - timestamp < RELOAD_WINDOW_MS);
  return recent.length >= MAX_RELOADS_PER_WINDOW;
}

function recordReload() {
  const now = Date.now();
  const recent = readReloadTimestamps().filter(timestamp => now - timestamp < RELOAD_WINDOW_MS);
  recent.push(now);
  safeSet(KEY_RELOAD_TIMESTAMPS, JSON.stringify(recent));
}

function isAutoReloadDisabled() {
  return safeGet(KEY_AUTO_RELOAD_DISABLED) === '1';
}

function disableAutoReload(reason) {
  safeSet(KEY_AUTO_RELOAD_DISABLED, '1');
  versionState.reloadBlocked = true;
  // Auto can no longer succeed on its own from here on -- show the manual
  // path immediately rather than waiting for the fallback delay.
  versionState.showUpdateBanner = true;
  captureException(new Error('app-version: auto-reload safeguard tripped'), {
    source: 'app-version',
    reason
  });
}

// Reload at most once per target buildId — if we already tried reloading to
// reach a given build and are still not running it (stale intermediary
// cache, a rolled-back deploy, …), never blindly retry the same target.
function alreadyAttemptedReloadFor(buildId) {
  return safeGet(KEY_TARGET_BUILD_ID) === buildId;
}

function markReloadAttempt(buildId) {
  safeSet(KEY_TARGET_BUILD_ID, buildId);
}

// Safari can satisfy location.reload() from its document cache even when a
// freshly fetched version.json has already told us a newer bundle exists.
// Navigating to the same route with a one-use query parameter forces a new
// document request, while keeping the route and its existing query/hash.
function reloadFreshDocument(targetBuildId) {
  const url = new URL(window.location.href);
  url.searchParams.set('__appver', targetBuildId || Date.now().toString(36));
  window.location.replace(url.toString());
}

// Called once at startup: if we reloaded expecting a specific build, confirm
// we actually landed on it. If not, stop trying — this is the loop breaker.
function verifyReloadOutcome() {
  const target = safeGet(KEY_TARGET_BUILD_ID);
  if (!target || !CURRENT_BUILD_ID) return;

  if (target === CURRENT_BUILD_ID) {
    // Success — clear so a *future* newer build is free to trigger a reload.
    safeRemove(KEY_TARGET_BUILD_ID);
  } else {
    disableAutoReload('reloaded but still running ' + CURRENT_BUILD_ID + ', expected ' + target);
  }
}

function performReload(targetBuildId) {
  if (!storageAvailable || isAutoReloadDisabled()) {
    versionState.reloadBlocked = true;
    return false;
  }
  if (alreadyAttemptedReloadFor(targetBuildId)) {
    disableAutoReload('already attempted reload for build ' + targetBuildId);
    return false;
  }
  if (reloadBudgetExceeded()) {
    disableAutoReload('exceeded ' + MAX_RELOADS_PER_WINDOW + ' reloads within the safety window');
    return false;
  }

  markReloadAttempt(targetBuildId);
  recordReload();
  reloadFreshDocument(targetBuildId);
  return true;
}

// --- Version polling ---------------------------------------------------------

function isPageVisible() {
  return document.visibilityState !== 'hidden';
}

async function fetchRemoteVersion() {
  try {
    const response = await fetch(VERSION_URL + '?t=' + Date.now(), {
      cache: 'no-store',
      credentials: 'same-origin'
    });
    if (!response.ok) return null;

    // Guards against an intermediary (rewrite fallback, misconfigured CDN)
    // silently returning index.html instead of a real version.json.
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) return null;

    const data = await response.json();
    if (!data || typeof data.buildId !== 'string' || !data.buildId) return null;
    return data.buildId;
  } catch (error) {
    // Network hiccup — stay quiet and try again on the next tick.
    return null;
  }
}

let lastCheckAt = 0;

async function checkForUpdate(source) {
  if (!CURRENT_BUILD_ID || !isPageVisible()) return;

  const now = Date.now();
  if (source === 'route' && now - lastCheckAt < ROUTE_CHECK_THROTTLE_MS) return;
  lastCheckAt = now;

  const remoteBuildId = await fetchRemoteVersion();
  if (!remoteBuildId) return;

  if (remoteBuildId === CURRENT_BUILD_ID) {
    versionState.updateAvailable = false;
    versionState.latestBuildId = null;
    return;
  }

  versionState.updateAvailable = true;
  versionState.latestBuildId = remoteBuildId;
  scheduleBannerFallback();
}

// Runs on every route change. Deliberately does NOT wait on a fresh network
// check — it acts on whatever versionState already knows, so it can't race
// against the (throttled, async) checkForUpdate call fired alongside it.
//
// No unsaved-form check here: navigating away from a document form in this
// app already discards any unsaved edits on its own (there's no
// beforeunload/route guard protecting them), so a reload here isn't adding
// any new risk beyond what navigating there already carried. Deliberately
// silent — the point is that a user who just saves and moves on never sees
// anything; see scheduleBannerFallback() for the case where this path never
// fires at all (tab left open, no further navigation).
function maybeAutoReloadOnRouteChange() {
  if (!versionState.updateAvailable || !versionState.latestBuildId) return;
  performReload(versionState.latestBuildId);
}

// Manual "update now" action for the banner button. This is the guaranteed
// recovery path the banner promises whenever auto-reload has been held back
// (a tripped safeguard, storage being unavailable, or simply that the tab
// hasn't navigated anywhere since the update was detected), so it
// deliberately bypasses every automatic-reload safeguard: it's an explicit,
// informed action a human just took, not a loop. It still records the
// attempt (when storage allows) purely so the next boot's
// verifyReloadOutcome()/telemetry stays accurate.
export function applyUpdateNow() {
  const targetBuildId = versionState.latestBuildId;
  if (storageAvailable) {
    if (targetBuildId) markReloadAttempt(targetBuildId);
    recordReload();
  }
  // Hide immediately in case the browser delays navigation (notably Safari
  // after returning to a long-running tab), then force a fresh document.
  versionState.showUpdateBanner = false;
  versionState.chunkErrorNotice = false;
  reloadFreshDocument(targetBuildId);
  return true;
}

// --- ChunkLoadError recovery --------------------------------------------------

function isChunkLoadError(error) {
  if (!error) return false;
  const name = (error && error.name) || '';
  const message = (error && error.message) || (typeof error === 'string' ? error : '') || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    // Defense in depth: a stale intermediary cache serving HTML at a JS URL
    // (see firebase.json hardening) surfaces as a parse error, not a
    // network error, when the browser tries to run it as a script.
    /Unexpected token '<'/i.test(message)
  );
}

function handleChunkLoadError(error, sourceLabel) {
  if (!isChunkLoadError(error)) return false;

  captureException(error, { source: 'app-version:chunk-load-error', trigger: sourceLabel });

  if (!storageAvailable || isAutoReloadDisabled()) {
    versionState.chunkErrorNotice = true;
    return true;
  }

  const alreadyRecovered = safeGet(KEY_CHUNK_ERROR_RECOVERED) === '1';
  if (alreadyRecovered) {
    versionState.chunkErrorNotice = true;
    disableAutoReload('repeated ChunkLoadError after a recovery reload was already used');
    return true;
  }

  if (reloadBudgetExceeded()) {
    versionState.chunkErrorNotice = true;
    disableAutoReload('ChunkLoadError but the reload safety budget is exhausted');
    return true;
  }

  safeSet(KEY_CHUNK_ERROR_RECOVERED, '1');
  recordReload();
  reloadFreshDocument();
  return true;
}

// --- Public entry point -------------------------------------------------------

export function initVersionCheck(router) {
  if (!CURRENT_BUILD_ID) return; // local dev serve without a baked build id

  verifyReloadOutcome();

  checkForUpdate('initial');

  document.addEventListener('visibilitychange', () => {
    if (isPageVisible()) checkForUpdate('visibility');
  });

  window.setInterval(() => checkForUpdate('poll'), POLL_INTERVAL_MS);

  if (router) {
    router.afterEach(() => {
      // Wait for this route's (possibly throttled/no-op) check to settle
      // before deciding whether to reload, so the very first navigation
      // that discovers a new build can act on it immediately instead of
      // reading stale state and waiting for the next navigation.
      checkForUpdate('route').then(() => {
        maybeAutoReloadOnRouteChange();
      });
    });

    router.onError(error => {
      handleChunkLoadError(error, 'router-onerror');
    });
  }

  window.addEventListener(
    'error',
    event => {
      const target = event.target;
      if (target && target.tagName === 'SCRIPT' && typeof target.src === 'string' && /\/js\//.test(target.src)) {
        handleChunkLoadError(new Error('ChunkLoadError: script failed to load: ' + target.src), 'window-error-script');
      } else if (event.error) {
        handleChunkLoadError(event.error, 'window-error');
      }
    },
    true
  );

  window.addEventListener('unhandledrejection', event => {
    if (handleChunkLoadError(event.reason, 'unhandled-rejection')) {
      event.preventDefault();
    }
  });
}
